import { v4 as uuidv4 } from 'uuid';

import { type AgentInboundMessagePill } from '@air/server-sdk';

import { type WorkspaceAgentComposerScopePill } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentComposerPills';
import { getWorkspaceAgentAssetSubjectKey } from '~/components/v2/components/workspace/workspace-agent-panel/utils/flattenWorkspaceAgentAssetPillItems';

/** Scope assets only. The open board is `visualContext`, not an outbound pill. */
const buildScopeOutboundPills = (pills: WorkspaceAgentComposerScopePill[]): AgentInboundMessagePill[] =>
  pills.flatMap((pill) =>
    pill.type === 'asset'
      ? [
          {
            assetId: pill.assetId,
            id: pill.id,
            type: 'asset',
            versionId: pill.versionId,
          } satisfies AgentInboundMessagePill,
        ]
      : [],
  );

/** Selected assets on the wire. View boards stay on `visualContext` (WORK-900). */
export const buildWorkspaceAgentComposerOutboundPills = (
  pills: WorkspaceAgentComposerScopePill[],
): AgentInboundMessagePill[] => buildScopeOutboundPills(pills);

const isWorkspaceAgentOutboundAssetPill = (
  pill: AgentInboundMessagePill,
): pill is Extract<AgentInboundMessagePill, { type: 'asset' }> => pill.type === 'asset';

const isWorkspaceAgentOutboundAssetListPill = (
  pill: AgentInboundMessagePill,
): pill is Extract<AgentInboundMessagePill, { type: 'assetList' }> => pill.type === 'assetList';

const isWorkspaceAgentOutboundBoardPill = (
  pill: AgentInboundMessagePill,
): pill is Extract<AgentInboundMessagePill, { type: 'board' }> => pill.type === 'board';

const isWorkspaceAgentOutboundBoardListPill = (
  pill: AgentInboundMessagePill,
): pill is Extract<AgentInboundMessagePill, { type: 'boardList' }> => pill.type === 'boardList';

type OutboundAssetSubject = {
  assetId: string;
  pillId: string;
  versionId?: string;
};

type OutboundBoardSubject = {
  boardId: string;
  pillId: string;
};

type OutboundAssetPill = Extract<AgentInboundMessagePill, { type: 'asset' }>;
type OutboundBoardPill = Extract<AgentInboundMessagePill, { type: 'board' }>;

const EMPTY_WORKSPACE_AGENT_OUTBOUND_CITED_PILL_IDS = new Set<string>();

const isCitedWorkspaceAgentOutboundPill = (pillId: string, citedPillIds: ReadonlySet<string>): boolean =>
  citedPillIds.has(pillId);

const collectCitedWorkspaceAgentOutboundAssetPills = (
  pills: readonly AgentInboundMessagePill[],
  citedPillIds: ReadonlySet<string>,
): OutboundAssetPill[] => {
  const pillsBySubject = new Map<string, OutboundAssetPill>();

  for (const pill of pills) {
    if (!isWorkspaceAgentOutboundAssetPill(pill) || !isCitedWorkspaceAgentOutboundPill(pill.id, citedPillIds)) {
      continue;
    }

    pillsBySubject.set(getWorkspaceAgentAssetSubjectKey(pill.assetId, pill.versionId), pill);
  }

  return [...pillsBySubject.values()];
};

const collectCitedWorkspaceAgentOutboundBoardPills = (
  pills: readonly AgentInboundMessagePill[],
  citedPillIds: ReadonlySet<string>,
): OutboundBoardPill[] => {
  const pillsByBoardId = new Map<string, OutboundBoardPill>();

  for (const pill of pills) {
    if (!isWorkspaceAgentOutboundBoardPill(pill) || !isCitedWorkspaceAgentOutboundPill(pill.id, citedPillIds)) {
      continue;
    }

    pillsByBoardId.set(pill.boardId, pill);
  }

  return [...pillsByBoardId.values()];
};

/**
 * Walks uncited pills in order: standalone entries last-win per subject; list members fill only new
 * subjects. Subjects already covered by a cited pill are skipped.
 */
const collectUncitedWorkspaceAgentOutboundAssetSubjects = (
  pills: readonly AgentInboundMessagePill[],
  citedSubjectKeys: ReadonlySet<string>,
  citedPillIds: ReadonlySet<string>,
): OutboundAssetSubject[] => {
  const subjectsByKey = new Map<string, OutboundAssetSubject>();

  for (const pill of pills) {
    if (!isWorkspaceAgentOutboundAssetPill(pill) || isCitedWorkspaceAgentOutboundPill(pill.id, citedPillIds)) {
      continue;
    }

    const subjectKey = getWorkspaceAgentAssetSubjectKey(pill.assetId, pill.versionId);

    if (citedSubjectKeys.has(subjectKey)) {
      continue;
    }

    subjectsByKey.set(subjectKey, {
      assetId: pill.assetId,
      pillId: pill.id,
      versionId: pill.versionId,
    });
  }

  for (const pill of pills) {
    if (!isWorkspaceAgentOutboundAssetListPill(pill) || isCitedWorkspaceAgentOutboundPill(pill.id, citedPillIds)) {
      continue;
    }

    for (const item of pill.items ?? []) {
      const subjectKey = getWorkspaceAgentAssetSubjectKey(item.assetId, item.versionId);

      if (citedSubjectKeys.has(subjectKey) || subjectsByKey.has(subjectKey)) {
        continue;
      }

      subjectsByKey.set(subjectKey, {
        assetId: item.assetId,
        pillId: pill.id,
        versionId: item.versionId,
      });
    }
  }

  return [...subjectsByKey.values()];
};

const collectUncitedWorkspaceAgentOutboundBoardSubjects = (
  pills: readonly AgentInboundMessagePill[],
  citedBoardIds: ReadonlySet<string>,
  citedPillIds: ReadonlySet<string>,
): OutboundBoardSubject[] => {
  const subjectsByKey = new Map<string, OutboundBoardSubject>();

  for (const pill of pills) {
    if (!isWorkspaceAgentOutboundBoardPill(pill) || isCitedWorkspaceAgentOutboundPill(pill.id, citedPillIds)) {
      continue;
    }

    if (citedBoardIds.has(pill.boardId)) {
      continue;
    }

    subjectsByKey.set(pill.boardId, {
      boardId: pill.boardId,
      pillId: pill.id,
    });
  }

  for (const pill of pills) {
    if (!isWorkspaceAgentOutboundBoardListPill(pill) || isCitedWorkspaceAgentOutboundPill(pill.id, citedPillIds)) {
      continue;
    }

    for (const item of pill.items ?? []) {
      if (citedBoardIds.has(item.boardId) || subjectsByKey.has(item.boardId)) {
        continue;
      }

      subjectsByKey.set(item.boardId, {
        boardId: item.boardId,
        pillId: pill.id,
      });
    }
  }

  return [...subjectsByKey.values()];
};

const toWorkspaceAgentOutboundAssetPill = (subject: OutboundAssetSubject): OutboundAssetPill => ({
  assetId: subject.assetId,
  id: subject.pillId,
  type: 'asset',
  ...(subject.versionId ? { versionId: subject.versionId } : {}),
});

const toWorkspaceAgentOutboundBoardPill = (subject: OutboundBoardSubject): OutboundBoardPill => ({
  boardId: subject.boardId,
  id: subject.pillId,
  type: 'board',
});

const buildUncitedWorkspaceAgentOutboundAssetPills = (
  subjects: readonly OutboundAssetSubject[],
): AgentInboundMessagePill[] => {
  if (subjects.length === 0) {
    return [];
  }

  if (subjects.length === 1) {
    const [only] = subjects;

    return [toWorkspaceAgentOutboundAssetPill(only)];
  }

  return [
    {
      id: uuidv4(),
      items: subjects.map(({ assetId, versionId }) => ({
        assetId,
        ...(versionId ? { versionId } : {}),
      })),
      type: 'assetList',
    },
  ];
};

const buildUncitedWorkspaceAgentOutboundBoardPills = (
  subjects: readonly OutboundBoardSubject[],
): AgentInboundMessagePill[] => {
  if (subjects.length === 0) {
    return [];
  }

  if (subjects.length === 1) {
    const [only] = subjects;

    return [toWorkspaceAgentOutboundBoardPill(only)];
  }

  return [
    {
      id: uuidv4(),
      items: subjects.map(({ boardId }) => ({ boardId })),
      type: 'boardList',
    },
  ];
};

/**
 * Cited `asset` / `board` pills stay singletons. Uncited subjects collapse to one `asset`/`board`
 * or one `assetList`/`boardList`. The send path lifts those lists into session artifacts.
 */
export const normalizeWorkspaceAgentOutboundPills = (
  pills: readonly AgentInboundMessagePill[],
  citedPillIds: ReadonlySet<string> = EMPTY_WORKSPACE_AGENT_OUTBOUND_CITED_PILL_IDS,
): AgentInboundMessagePill[] => {
  const otherPills = pills.filter(
    (pill) =>
      !isWorkspaceAgentOutboundAssetPill(pill) &&
      !isWorkspaceAgentOutboundAssetListPill(pill) &&
      !isWorkspaceAgentOutboundBoardPill(pill) &&
      !isWorkspaceAgentOutboundBoardListPill(pill),
  );

  const citedAssetPills = collectCitedWorkspaceAgentOutboundAssetPills(pills, citedPillIds);
  const citedBoardPills = collectCitedWorkspaceAgentOutboundBoardPills(pills, citedPillIds);
  const citedAssetSubjectKeys = new Set(
    citedAssetPills.map((pill) => getWorkspaceAgentAssetSubjectKey(pill.assetId, pill.versionId)),
  );
  const citedBoardIds = new Set(citedBoardPills.map((pill) => pill.boardId));

  return [
    ...otherPills,
    ...citedAssetPills,
    ...buildUncitedWorkspaceAgentOutboundAssetPills(
      collectUncitedWorkspaceAgentOutboundAssetSubjects(pills, citedAssetSubjectKeys, citedPillIds),
    ),
    ...citedBoardPills,
    ...buildUncitedWorkspaceAgentOutboundBoardPills(
      collectUncitedWorkspaceAgentOutboundBoardSubjects(pills, citedBoardIds, citedPillIds),
    ),
  ];
};
